import { FinaliseHTSCurationModal } from '@/components/WorkflowModals/FinaliseHTSCurationModal';
import { chipColours } from '@/themes/colours';
import { IAnalysisSet } from '@/types/Analysis/AnalysisSets.types';
import { IHTSCulture } from '@/types/HTS.types';
import { FinaliseCurationModal } from '../../components/WorkflowModals/FinaliseCurationModal';
import WarningPopup from '../../components/WorkflowModals/WarningPopup';
import {
  CurationStatus,
  FailedStatusReason,
  HtsStatus,
  ICurrentCurationStatus,
  ICurrentStatus,
  SecondaryCurationStatus,
} from '../../types/Samples/Sample.types';

type CurationStatuses = {
  [key in CurationStatus]: ICurrentCurationStatus;
}

type SecondaryCurationStatuses = {
  [key in SecondaryCurationStatus]: ICurrentStatus<SecondaryCurationStatus, IAnalysisSet>;
}

type HtsStatuses = {
  [key in HtsStatus]: ICurrentStatus<HtsStatus, IHTSCulture[]>;
}

export const curationStatuses: CurationStatuses = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Sequencing: {
    status: 'Sequencing',
    chipProps: {
      status: 'Sequencing',
      color: chipColours.chipGreyText,
      backgroundColor: chipColours.chipGreyBg,
    },
    readonly: true,
    nextStatuses: [
      {
        status: 'Ready to Start',
        label: 'Ready to Start',
        scope: 'curation.status.write',
      },
      {
        status: 'In Pipeline',
        label: 'In Pipeline',
        scope: 'curation.status.write',
      },
    ],
  },
  'In Pipeline': {
    status: 'In Pipeline',
    chipProps: {
      status: 'In Pipeline',
      color: chipColours.chipGreyText,
      backgroundColor: chipColours.chipGreyBg,
    },
    readonly: true,
    nextStatuses: [
      {
        status: 'Ready to Start',
        label: 'Ready to Start',
        scope: 'curation.status.write',
      },
    ],
  },
  'Ready to Start': {
    status: 'Ready to Start',
    chipProps: {
      status: 'Ready',
      color: chipColours.chipGreenText,
      backgroundColor: chipColours.chipGreenBg,
    },
    readonly: false,
    nextStatuses: [
      {
        status: 'In Progress',
        label: 'Start Curation',
        scope: 'curation.sample.assigned.write',
        modal: WarningPopup,
        secondaryStatus: 'Not Started',
      },
    ],
  },
  'In Progress': {
    status: 'In Progress',
    chipProps: {
      status: 'In Progress',
      color: chipColours.chipBlueText,
      backgroundColor: chipColours.chipBlueBg,
    },
    readonly: false,
    nextStatuses: [
      {
        status: 'Done',
        label: 'Finalise Curation',
        scope: 'curation.sample.assigned.write',
        modal: FinaliseCurationModal,
      },
    ],
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Done: {
    status: 'Done',
    chipProps: {
      status: 'Done',
      color: chipColours.chipGreyText,
      backgroundColor: chipColours.chipGreyBg,
    },
    readonly: false,
    nextStatuses: [
      {
        status: 'Done',
        label: 'Re-export clinical data',
        scope: 'curation.sample.assigned.write',
        modal: FinaliseCurationModal,
      },
    ],
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Failed: {
    status: 'Failed',
    chipProps: {
      status: 'Failed',
      color: chipColours.chipRedText,
      backgroundColor: chipColours.chipRedBg,
    },
    readonly: true,
    nextStatuses: [],
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Withdrawn: {
    status: 'Withdrawn',
    chipProps: {
      status: 'Withdrawn',
      color: chipColours.chipGreyText,
      backgroundColor: chipColours.chipGreyBg,
    },
    readonly: false,
    nextStatuses: [],
  },
};

export const failedStatusReasons: FailedStatusReason[] = ['Contaminated Germline', 'No Donor', 'No Tumour'];

export const secondaryCurationStatuses: SecondaryCurationStatuses = {
  'Not Started': {
    status: 'Not Started',
    canProgress: (analysisSet?: IAnalysisSet) => analysisSet?.curationStatus === 'In Progress',
    nextStatus: {
      status: 'In Progress',
      label: 'Send for Review',
    },
    readonly: true,
  },
  'In Progress': {
    status: 'In Progress',
    canProgress: () => true,
    nextStatus: {
      status: 'Completed',
      label: 'Complete Review',
    },
    readonly: false,
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Completed: {
    status: 'Completed',
    canProgress: () => false,
    readonly: true,
  },
};

export const htsStatuses: HtsStatuses = {
  'Not Started': {
    status: 'Not Started',
    canProgress: () => true,
    nextStatus: {
      status: 'In Progress',
      label: 'Start HTS Curation',
    },
    readonly: true,
  },
  'In Progress': {
    status: 'In Progress',
    canProgress: (cultures) => (
      Boolean(cultures?.every((c) => c.screenStatus === 'PASS' || c.screenStatus === 'FAIL'))
    ),
    nextStatus: {
      status: 'Completed',
      label: 'Complete HTS Curation',
      modal: FinaliseHTSCurationModal,
    },
    readonly: false,
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Completed: {
    status: 'Completed',
    canProgress: () => false,
    readonly: true,
  },
};
